import { Component, ComponentFactoryResolver, ViewChild } from "@angular/core";

import proj4 from "proj4";

import View from "ol/View";
import { register } from "ol/proj/proj4";
import { Group, Tile } from "ol/layer";
import { OSM, TileWMS, XYZ } from "ol/source";
import { Vector as VectorSource } from "ol/source";
import VectorLayer from "ol/layer/Vector";
import { boundingExtent } from "ol/extent";
import { fromExtent } from "ol/geom/Polygon";
import Feature from "ol/Feature";
import { transform, transformExtent, get as getProjection } from "ol/proj";
import { Fill, Stroke, Style } from "ol/style";

import {
  HsDialogItem,
  HsConfig,
  HsPanelContainerService,
  HsSidebarService,
  HsLanguageService,
  HsMapService,
  HsLayerManagerService,
  HsDialogContainerService,
  HsLayoutService,
} from "hslayers-ng";

import { AppService } from "./app.service";
import { AcAboutComponent } from "./about/about.component";
import { AcStrategyComponent } from "./strategy/strategy.component";
import { StrategyCommonService } from "./strategy/strategy-common.service";
import { AcMapOverlayComponent } from "./map-overlay/map-overlay.component";
import * as layerDefsData from "assets/layers.json";

import { translationOverrides } from "./translations";
import { Geometry } from "ol/geom";
import { Subject } from "rxjs";
import { NgbDate } from "@ng-bootstrap/ng-bootstrap";
import { Options, ChangeContext } from "@angular-slider/ngx-slider";

const layerDefs = (layerDefsData as any).default;
proj4.defs(
  "EPSG:5514",
  "+proj=krovak +lat_0=49.5 +lon_0=24.83333333333333 +alpha=30.28813972222222 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=542.5,89.2,456.9,5.517,2.275,5.516,6.96 +units=m +no_defs"
);
register(proj4);
const sjtskProjection = getProjection("EPSG:5514");
sjtskProjection.setExtent([
  -1563456.8248796, -1601971.7482254, 740007.6022939, 256379.1082822,
]);

@Component({
  selector: "app-component",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent {
  dateTemplate = "-01-01T00:00:00.000Z";
  dateRangeLoads: Subject<any> = new Subject();
  dataRange = [1982, 2020];

  constructor(
    public AppService: AppService,
    public HsConfig: HsConfig,
    public hsLangService: HsLanguageService,
    private hsMapService: HsMapService,
    public hsLayerManagerService: HsLayerManagerService,
    private hsDialogContainerService: HsDialogContainerService,
    private HsSidebarService: HsSidebarService,
    private hsPanelContainerService: HsPanelContainerService,
    private StrategyCommonService: StrategyCommonService,
    private HsLayoutService: HsLayoutService,
    private componentFactoryResolver: ComponentFactoryResolver
  ) {
    this.hsLangService.setLanguage("cs");

    this.HsConfig.update({
      useProxy: true,
      saveMapStateOnReload: false,
      assetsPath: "/static/clima-map/assets/hslayers-ng",
      proxyPrefix: window.location.hostname.includes("localhost")
        ? `${window.location.protocol}//${window.location.hostname}:8085/`
        : "/proxy/",
      searchProvider: (q) => {
        return `/proxy/http://api.geonames.org/searchJSON?&name_startsWith=${q}`;
      },
      open_lm_after_comp_loaded: true,
      default_layers: this.loadLayers(),
      componentsEnabled: {
        sidebar: true,
        toolbar: false,
        guiOverlay: true,
        drawToolbar: false,
        searchToolbar: true,
        measureToolbar: false,
        sensors: false,
        crossfilter: false,
        geolocationButton: false,
        tracking: false,
        mapControls: true,
        basemapGallery: true,
        modal: true,
        dialog: true,
        defaultViewButton: false,
      },
      panelsEnabled: {
        composition_browser: false,
        addData: false,
        mobile_settings: true,
        measure: false,
        draw: false,
        print: false,
        saveMap: false,
        language: true,
        sensors: false,
        feature_crossfilter: false,
        compositionLoadingProgress: false,
        info: false,
        legend: false,
        layermanager: false,
      },
      status_manager_url: "https://www.agrihub.cz/share/",
      datasources: [
        {
          title: "Micka",
          url: "https://www.agrihub.cz/micka/csw",
          language: "eng",
          type: "micka",
        },
        {
          title: "Layman",
          liferayProtocol: "https",
          url: "https://www.agrihub.cz/hsl-layman",
          user: "browser",
          type: "layman",
        },
      ],
      default_view: new View({
        projection: getProjection("EPSG:5514"),
        center: transform([15.628, 49.864249], "EPSG:4326", "EPSG:5514"), //Latitude longitude    to S-JTSK
        extent: transformExtent(
          [9.832275, 46.151428, 21.423828, 53.57707],
          "EPSG:4326",
          "EPSG:5514"
        ),
        multiWorld: false,
        zoom: 3,
      }),
      translationOverrides,
      panelWidths: {
        strategy: 850,
      },
    });
    this.hsMapService.HsLayoutService.setDefaultPanel("strategy");

    //Temporary hack until API request is available
    this.getApiDateRange().then((data: any) => {
      for (let i = data.range[0]; i <= data.range[1]; i++) {
        this.AppService.dateRange.push(i + this.dateTemplate);
      }
      this.AppService.timelineOptions = Object.assign(
        {},
        this.AppService.timelineOptions,
        {
          ceil: data.range[1] - data.range[0],
          translate: (value: number): string => {
            return new Date(this.AppService.dateRange[value])
              .getFullYear()
              .toString();
          },
        }
      );
      this.AppService.currentDateIndexUpper = this.AppService.dateRange.indexOf(
        "2017-01-01T00:00:00.000Z"
      );
      this.dateRangeLoads.next(this.AppService.dateRange);
    });
  }

  dateRangeloaded() {
    return new Promise<any>((resolve, reject) => {
      if (this.AppService.dateRange.length > 0) {
        resolve(this.AppService.dateRange);
        return;
      } else {
        this.dateRangeLoads.subscribe((map) => {
          if (map) {
            resolve(map);
          }
        });
      }
    });
  }

  getApiDateRange() {
    return new Promise((resolve, reject) => {
      resolve({
        range: this.dataRange,
      });
    });
  }

  ngOnInit(): void {
    const componentFactory =
      this.componentFactoryResolver.resolveComponentFactory(
        AcMapOverlayComponent
      );

    this.HsLayoutService.mapSpaceRef.subscribe((mapSpace) => {
      if (mapSpace) {
        mapSpace.createComponent(componentFactory);
      }
    });

    this.hsPanelContainerService.create(AcStrategyComponent, {});
    this.HsSidebarService.buttons.push({
      panel: "strategy",
      order: -1,
      title: () => this.hsLangService.getTranslation("STRATEGY.panelTitle"),
      description: () =>
        this.hsLangService.getTranslation("STRATEGY.panelDescription"),
      icon: "icon-alertpay",
    });

    this.dateRangeloaded().then((dateRange) => {
      this.StrategyCommonService.selectedDate.upper = parseInt(
        dateRange[this.AppService.currentDateIndexUpper].slice(0, 4)
      );
      this.StrategyCommonService.selectedDate.lower = parseInt(
        dateRange[this.AppService.currentDateIndexLower].slice(0, 4)
      );
      this.StrategyCommonService.calendarMax = {
        year: parseInt(dateRange[dateRange.length - 1].slice(0, 4)),
        month: 12,
        day: 31,
      };
      this.StrategyCommonService.calendarMin = {
        year: parseInt(dateRange[0].slice(0, 4)),
        month: 1,
        day: 1,
      };
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      let aboutDialog: HsDialogItem = this.hsDialogContainerService.create(
        AcAboutComponent,
        {
          message:
            "You do not have access rights to see this model! Please contact the Admin or owner of the model to get access rights!",
          title: "Access denied!",
        }
      );
    }, 0);
  }

  loadLayers(): any[] {
    let layers = [];
    // fixed basemap
    layers.push(
      new Tile({
        source: new TileWMS({
          url: "https://ags.cuzk.cz/arcgis/services/zm/MapServer/WMSServer",
          params: {
            LAYERS: "0",
          },
        }),
        visible: false,
        properties: {
          inlineLegend: false,
          title: "Základní mapa ČR",
          base: true,
          removable: false,
          thumbnail: "https://www.agrihub.cz/hsl-ng/img/zmcr.jpg",
        },
      })
    );
    layers.push(
      new Tile({
        source: new OSM(),
        visible: true,
        properties: {
          inlineLegend: false,
          title: "OpenStreetMap",
          base: true,
          removable: false,
        },
      })
    );
    layers.push(
      new Tile({
        source: new TileWMS({
          url: "https://geoportal.cuzk.cz/WMS_ORTOFOTO_PUB/service.svc/get",
          params: {
            LAYERS: "GR_ORTFOTORGB",
          },
        }),
        visible: false,
        properties: {
          inlineLegend: false,
          title: "Ortofoto ČÚZK",
          base: true,
          removable: false,
          thumbnail: "https://www.agrihub.cz/hsl-ng/img/orto.jpg",
        },
      })
    );

    // dynamically load other layers
    for (let i = 0; i < layerDefs.length; i++) {
      layers.push(
        new Tile({
          source: new TileWMS({
            url: layerDefs[i].url,
            params: {
              LAYERS: layerDefs[i].layers,
              INFO_FORMAT: "text/html",
              FORMAT: "image/png; mode=8bit",
            },
            crossOrigin: null,
          }),
          visible: layerDefs[i].visible ?? false,
          properties: {
            queryCapabilities: false,
            inlineLegend: false,
            title: layerDefs[i].title,
            exclusive: true,
            getFeatureInfoTarget: "info-panel",
            path: layerDefs[i].path,
            dimensions: layerDefs[i].dimensions,
            abstract: layerDefs[i].abstract,
          },
        })
      );
    }
    layers.push(this.addDataBboxLayer());

    return layers;
  }

  addDataBboxLayer(): VectorLayer<VectorSource<Geometry>> {
    const style = new Style({
      fill: new Fill({
        color: "transparent",
      }),
      stroke: new Stroke({
        color: "#319FD3",
        width: 1,
      }),
    });
    const bboxLayer = new VectorLayer({
      source: new VectorSource(),
      visible: true,
      properties: {
        inlineLegend: false,
        title: "cfd",
        base: false,
        removable: false,
        style: style,
        queryable: false,
        // showInLayerManager: false,
        path: "Data",
      },
    });
    const extent = boundingExtent(
      this.StrategyCommonService.dataBbox.find(
        (item) => item.location == this.StrategyCommonService.selectedLocation
      ).bbox
    );
    const polygon = fromExtent(
      transformExtent(extent, "EPSG:4326", "EPSG:5514")
    );
    const feature = new Feature(polygon);
    bboxLayer.getSource().addFeature(feature);

    return bboxLayer;
  }

  changeDate(event: ChangeContext): void {
    const highValueChanged =
      event.highValue != this.AppService.previouscurrentDateIndexUpper;

    this.StrategyCommonService.selectedDate.upper = parseInt(
      this.AppService.dateRange[this.AppService.currentDateIndexUpper].slice(
        0,
        4
      )
    );
    this.StrategyCommonService.selectedDate.lower = parseInt(
      this.AppService.dateRange[this.AppService.currentDateIndexLower].slice(
        0,
        4
      )
    );

    this.AppService.updateLayerParams.next({
      year: this.StrategyCommonService.selectedDate.upper,
    });

    console.log(
      "date changed: " +
        this.AppService.dateRange[this.AppService.currentDateIndexUpper]
    );

    if (
      this.HsLayoutService.mainpanel == "strategy" &&
      this.StrategyCommonService.selectedCoords &&
      (highValueChanged || this.StrategyCommonService.dataFrequency == "year")
    ) {
      this.StrategyCommonService.seasonStartDate = new NgbDate(
        this.StrategyCommonService.selectedDate.upper,
        this.StrategyCommonService.seasonStartDate.month,
        this.StrategyCommonService.seasonStartDate.day
      );
      this.StrategyCommonService.fetchAndRender(
        this.StrategyCommonService.selectedCoords
      );
    }
    this.AppService.previouscurrentDateIndexUpper =
      this.AppService.currentDateIndexUpper;
  }
}
